import type { TextComposerExtension } from "./extensionContract";
import {
  buildAuthoringDraftSchema,
  composeNormalize,
} from "./extensionRegistry";
import { defaultExtensions } from "../extensions/defaultExtensions";
import type { z } from "zod";

/**
 * Open authoring-state interface. The core only declares `bodyText`.
 * Built-in and userland extensions augment this interface via TypeScript
 * declaration merging from their own files (see `extensions/cta/ctaExtension.ts`,
 * `extensions/media/mediaExtension.ts`).
 */
export interface TextComposerAuthoringState {
  bodyText?: string;
}

/** True when the normalized authoring draft has no keys (no body, media, CTA, etc.). */
export function isNormalizedAuthoringStateEmpty(
  state: TextComposerAuthoringState,
): boolean {
  return Object.keys(state).length === 0;
}

/**
 * Default authoring-state schema built from `defaultExtensions`. Built lazily
 * on first access so module-load cycles via extension files (e.g. components
 * that import the store) don't observe an uninitialised `defaultExtensions`.
 */
let cachedDefaultSchema: z.ZodType<TextComposerAuthoringState> | undefined;

export function getDefaultAuthoringDraftSchema(): z.ZodType<TextComposerAuthoringState> {
  if (!cachedDefaultSchema) {
    cachedDefaultSchema = buildAuthoringDraftSchema(defaultExtensions);
  }
  return cachedDefaultSchema;
}

/**
 * Validates and trims the authoring draft using the registered extension set.
 * Extensions whose slices are empty (per `isEmpty`) are dropped.
 */
export function normalizeAuthoringDraft(
  draft: TextComposerAuthoringState,
  extensions: TextComposerExtension[] = defaultExtensions,
): TextComposerAuthoringState {
  const schema =
    extensions === defaultExtensions
      ? getDefaultAuthoringDraftSchema()
      : buildAuthoringDraftSchema(extensions);
  const parsed = schema.parse(draft);
  return composeNormalize(extensions)(parsed);
}
